/**
 * Piezo Witness Workflow — Execution Context
 *
 * Witness V2 is mandatory for OpsTumbler: every action requires a Witness.CreateObservation
 * call before bundle assembly. The resulting observationDocuments are included in the artifact
 * bundle, which triggers the V2 verification path on the backend.
 */

import { bytesToEncoded, CreateObservationResult } from '@amzn/piezo-typescript-thick-client';
import { PiezoDebugPrefix } from './PiezoClient.constants';
import { ExecuteWitnessWorkflowInput, ExecuteWitnessWorkflowOutput } from './PiezoClient.types';

/**
 * Executes the Witness workflow by creating an observation from the provided action info document
 *
 * @param inputs - The inputs required to execute the Witness workflow, including action info,
 *   Plenum client, Witness client, and Witness deckbox configuration.
 * @returns The observed action info, observation deckbox, and observation documents
 */
export const executeWitnessWorkflow = async ({
  actionInfo,
  plenumClient,
  witnessClient,
  witnessDeckbox,
}: ExecuteWitnessWorkflowInput): Promise<ExecuteWitnessWorkflowOutput> => {
  if (!actionInfo) {
    throw new Error('Action info doc is required to execute the Witness workflow');
  }

  console.debug(`${PiezoDebugPrefix} Calling Witness createObservation`);
  const actionInfoBytes = plenumClient.decodePlenumDocument(actionInfo);
  const createObservationResult: CreateObservationResult = await witnessClient.createObservation(actionInfoBytes);

  console.debug(
    `${PiezoDebugPrefix} Witness observation created - ObservationId: ${createObservationResult.observationId}, region: ${createObservationResult.region}`,
  );
  const observedActionInfo: string = bytesToEncoded(createObservationResult.observedActionInfoDocument);
  const observationDocument: string = bytesToEncoded(createObservationResult.observationDocument);
  const observationDeckbox: string[] | undefined = witnessDeckbox[createObservationResult.region];

  if (!observationDeckbox || observationDeckbox.length === 0) {
    throw new Error(`No Witness deckbox available for observation region ${createObservationResult.region}`);
  }

  return {
    observedActionInfo,
    observationDeckbox,
    observationDocuments: [observationDocument],
  };
};
